import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AuditLogTable = ({ logs }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7days');

  const actionOptions = [
    { value: 'all', label: 'All Actions' },
    { value: 'approval', label: 'Approvals' },
    { value: 'rejection', label: 'Rejections' },
    { value: 'suspension', label: 'Suspensions' },
    { value: 'reveal', label: 'Reveals' }
  ];

  const dateOptions = [
    { value: '24hours', label: 'Last 24 Hours' },
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: 'all', label: 'All Time' }
  ];

  const getActionBadge = (action) => {
    const badges = {
      approval: 'bg-success/10 text-success',
      rejection: 'bg-error/10 text-error',
      suspension: 'bg-warning/10 text-warning',
      reveal: 'bg-accent/10 text-accent',
      update: 'bg-primary/10 text-primary'
    };
    return badges?.[action] || 'bg-muted text-muted-foreground';
  };

  const getActionIcon = (action) => {
    const icons = {
      approval: 'Check',
      rejection: 'X',
      suspension: 'Ban',
      reveal: 'Shield',
      update: 'Edit'
    };
    return icons?.[action] || 'Activity';
  };

  const filteredLogs = logs?.filter(log => {
    const matchesSearch = log?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.adminName?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesAction = actionFilter === 'all' || log?.action === actionFilter;
    return matchesSearch && matchesAction;
  });

  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 elevation-sm">
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search audit logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full"
          />
        </div>
        <div className="flex gap-2">
          <Select
            options={actionOptions}
            value={actionFilter}
            onChange={setActionFilter}
            placeholder="Filter by action"
            className="w-full sm:w-40"
          />
          <Select
            options={dateOptions}
            value={dateRange}
            onChange={setDateRange}
            placeholder="Date range"
            className="w-full sm:w-40"
          />
        </div>
      </div>
      <div className="space-y-3">
        {filteredLogs?.map((log) => (
          <div key={log?.id} className="flex items-start gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors">
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
              <Icon name={getActionIcon(log?.action)} size={16} className="text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="text-sm text-foreground line-clamp-2">{log?.description}</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getActionBadge(log?.action)}`}>
                  {log?.action}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <Image 
                    src={log?.adminAvatar} 
                    alt={log?.adminAvatarAlt}
                    className="w-4 h-4 rounded-full object-cover"
                  />
                  <span className="text-xs text-muted-foreground">{log?.adminName}</span>
                </div>
                <span className="text-xs text-muted-foreground">•</span>
                <span className="text-xs text-muted-foreground">{log?.timestamp}</span>
                {log?.ipAddress && (
                  <>
                    <span className="text-xs text-muted-foreground">•</span>
                    <span className="text-xs text-muted-foreground font-mono">{log?.ipAddress}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredLogs?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No audit logs found matching your criteria</p>
        </div>
      )}
    </div>
  );
};

export default AuditLogTable;